import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { notificationKindLabel } from '../status-labels.js'

/**
 * What the shop has to act on. §6.26 `MSG-005a`.
 *
 * **This is the whole of v1's in-app channel.** `V1-SPEC.md` §2 puts WhatsApp and SMS in v3 and
 * `message.channel` is constrained to `('email', 'in_app')`, so in-app is one of the two things a
 * shop has, and nothing on the counter read `GET /notifications`. A till variance above threshold,
 * a lab query nobody answered, a job past its promised date: all of it was being recorded and none
 * of it was reaching a person.
 *
 * **Read and dismissed are different answers, and both are offered.** `packages/domain/src/
 * messaging.ts` says why: "'I have seen this' and 'this no longer needs doing' are different
 * answers. A shop that cannot say the second ends up with a permanent list it stops reading." So
 * the unread list has two buttons per row rather than one, and the tabs let somebody go back and
 * find what they dismissed.
 *
 * **One status per request**, because that is the endpoint's shape: it takes a status and defaults
 * to `unread`. Fetching all three and filtering here would make the client decide what counts as
 * outstanding, which is the server's answer to give.
 *
 * `ARC-007`: a status change carries the version it read, so two people clearing the same
 * notification is a conflict rather than a silent double write.
 */

export interface NotificationData {
  readonly id: string
  /** Appendix E's `staff_notification_kind`. Eight of them. */
  readonly kind: string
  readonly status: string
  readonly summary: string
  readonly version: number
  /** Absent means the branch's rather than one person's. */
  readonly staffId?: string
  /**
   * What the notification is about. Carried for `onOpenSource`, and **not printed**: the pair is a
   * table name and a UUID, and a shopkeeper reading `cash_session` and
   * `01932c1e-0000-7000-8000-...` off a till learns nothing the `summary` has not already said.
   * `source_type` is free text in the database rather than an Appendix E enumeration, so there is
   * no word to show for it either.
   */
  readonly sourceType?: string
  readonly sourceId?: string
}

export type NotificationTab = 'unread' | 'read' | 'dismissed'

export interface NotificationsProps {
  readonly load: (status: NotificationTab) => Promise<readonly NotificationData[]>
  readonly onOpenSource?: (sourceType: string, sourceId: string) => void
}

export function Notifications({ load, onOpenSource }: NotificationsProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [tab, setTab] = useState<NotificationTab>('unread')
  const [rows, setRows] = useState<readonly NotificationData[] | null>(null)
  /** Only reads fail here: the writes have no validation, so this is never a form message. */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load(tab))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : t('notifications.loadFailed'))
    }
  }, [load, tab, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // A settled write is what makes the list right again: the row that moved is no longer in this
  // tab, and somebody else may have cleared another one since.
  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    void refresh()
  })

  const setStatus = useCallback(
    async (row: NotificationData, status: 'read' | 'dismissed') => {
      const write = (await enqueue({
        method: 'POST',
        path: `/notifications/${row.id}/status`,
        label: t('notifications.queueLabel'),
        body: {
          status,
          // `ARC-007`: the version this screen read. Two people clearing the same notification is
          // a conflict with the current state rather than a second write over the first.
          expectedVersion: row.version,
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.notification'))

  return (
    <section className="lm-screen" aria-labelledby="notifications-heading">
      <h1 id="notifications-heading" className="lm-screen__heading">
        {t('notifications.heading')}
      </h1>

      <div className="lm-tabs" role="group" aria-label={t('notifications.tabsLabel')}>
        {(['unread', 'read', 'dismissed'] as const).map((which) => (
          <button
            key={which}
            type="button"
            className="lm-button lm-button--secondary"
            aria-pressed={tab === which}
            onClick={() => {
              setTab(which)
            }}
          >
            {which === 'unread'
              ? t('notifications.tabUnread')
              : which === 'read'
                ? t('notifications.tabRead')
                : t('notifications.tabDismissed')}
          </button>
        ))}
      </div>

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        The tabs are the filter. To do is the default view, so nothing there is good news (`empty`);
        Seen and Cleared are looking back, so nothing there is a filter that matched nothing.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  setRows(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: tab === 'unread' ? 'empty' : 'filtered' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('notifications.what')}
        emptyMessage={t('notifications.noneUnread')}
        filteredMessage={
          tab === 'read' ? t('notifications.noneSeen') : t('notifications.noneCleared')
        }
      >
        <ul className="lm-notification-list">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="lm-notification">
              <span className="lm-notification__kind">{notificationKindLabel(t, row.kind)}</span>
              {/* The server's own sentence. It names the till, the job or the amount, which is what
                  makes the notification actionable rather than a category. */}
              <span className="lm-notification__summary">{row.summary}</span>
              {row.staffId === undefined ? (
                <span className="lm-notification__whose">{t('notifications.branchWide')}</span>
              ) : null}

              {row.sourceType !== undefined && row.sourceId !== undefined && onOpenSource ? (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    onOpenSource(row.sourceType ?? '', row.sourceId ?? '')
                  }}
                >
                  {t('notifications.open')}
                </button>
              ) : null}

              {/* Only on the unread tab, and both verbs, because `STAFF_NOTIFICATION_TRANSITIONS`
                  allows `unread -> read` and `unread -> dismissed` and they mean different things. */}
              {row.status === 'unread' ? (
                <>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => void setStatus(row, 'read')}
                  >
                    {t('notifications.markRead')}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => void setStatus(row, 'dismissed')}
                  >
                    {t('notifications.dismiss')}
                  </button>
                </>
              ) : null}

              {row.status === 'read' ? (
                // `read -> dismissed` is the remaining edge: seen, and now done with.
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => void setStatus(row, 'dismissed')}
                >
                  {t('notifications.dismiss')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </DataSurface>
    </section>
  )
}
